import { useEffect, useState } from "react";
import { useRecoilState, useSetRecoilState } from "recoil";

import { formatPlaylistClip } from "MVVM/Views/EmbeddablePlaylist/Utilities";
import {
  PlaylistClipsState,
  ActivePlaylistClipState,
  DisplayEmptyState,
  PlaylistIdState,
} from "MVVM/State/embeddablePlaylist";
import { PlaylistClip } from "MVVM/Views/EmbeddablePlaylist/@types";

export type TagItem = {
  key: string;
  value: string;
  clips: PlaylistClip[];
};

export type TagClipMap = TagItem[];

type PlaylistFetchParams = {
  playlist: string;
  platform: string;
};

export const TRNEmbeddablePlaylistViewModel = ({
  playlist,
  platform,
}: PlaylistFetchParams) => {
  const [playlistClips, setPlaylistClips] = useRecoilState(PlaylistClipsState);
  const [filters, setFilters] = useState<TagClipMap>([]);
  const [activePlaylistClip, setActivePlaylistClip] = useRecoilState(
    ActivePlaylistClipState,
  );
  const setPlaylistId = useSetRecoilState(PlaylistIdState);
  const setDisplayEmptyState = useSetRecoilState(DisplayEmptyState);

  useEffect(() => {
    async function fetchData() {
      let finalPlaylist = playlist;
      let finalPlatform = platform;

      if (playlist === "auto") {
        try {
          const autoResponse = await fetch(
            "https://prt.allstar.gg/fortnite/clips/playlist?sort=score&limit=25&age=30d",
            {
              headers: {
                "X-API-Key": process.env.REACT_APP_TRN_PUBLIC_KEY || "",
                "Content-Type": "application/json",
              },
            },
          );

          const autoData = await autoResponse.json();

          if (autoData?.data?.playlistUrl) {
            const url = new URL(autoData.data.playlistUrl);
            const playlistParam = url.searchParams.get("playlist");
            const platformParam = url.searchParams.get("platform");

            if (playlistParam && platformParam) {
              finalPlaylist = playlistParam;
              finalPlatform = platformParam;
            }
          }
        } catch (error) {
          console.error("Error fetching auto playlist:", error);
          setDisplayEmptyState(true);
          return;
        }
      }

      const clipUrl = `${
        process.env.REACT_APP_PARTNER_URL
      }/playlist?name=${finalPlaylist}&platform=${encodeURIComponent(
        finalPlatform,
      )}&fmt=iframe`;

      const res = await fetch(clipUrl, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      const data = await res.json();

      if (!data?.data?.clips?.length) {
        setDisplayEmptyState(true);
        return;
      }

      setPlaylistId(finalPlaylist);

      const firstClip = data.data.clips[0];
      const formattedClip = formatPlaylistClip(firstClip);
      const isFallback = data.fallback;

      if (isFallback) setDisplayEmptyState(true);
      else setActivePlaylistClip(formattedClip);

      setPlaylistClips(data.data.clips);
    }

    fetchData();
  }, [
    platform,
    playlist,
    setActivePlaylistClip,
    setPlaylistClips,
    setDisplayEmptyState,
    setPlaylistId,
  ]);

  useEffect(() => {
    const tagMap: { [uniqueKey: string]: TagItem } = {};
    if (Array.isArray(playlistClips)) {
      for (const clip of playlistClips) {
        if (!clip || !clip.metadata) continue;
        for (const meta of clip.metadata) {
          if (
            typeof meta === "object" &&
            meta !== null &&
            "key" in meta &&
            "value" in meta
          ) {
            const key = (meta as { key: string; value: string }).key;
            const value = (meta as { key: string; value: string }).value;
            if (!value) continue;

            if (!shouldIncludeAsTag(key, value)) {
              continue;
            }

            const displayValue = formatMetadataValue(key, value);
            const uniqueKey = `${key}:${value}`;

            if (!tagMap[uniqueKey]) {
              tagMap[uniqueKey] = {
                key,
                value: displayValue,
                clips: [],
              };
            }
            tagMap[uniqueKey].clips.push(clip);
          }
        }
      }
      const tagsArray = Object.values(tagMap);
      setFilters(tagsArray);
    }
  }, [playlistClips]);

  const getClipsForTag = (tagKey: string): PlaylistClip[] => {
    if (tagKey === "all") {
      return playlistClips || [];
    }

    const tag = filters.find((tag) => `${tag.key}:${tag.value}` === tagKey);
    return tag?.clips || [];
  };

  return {
    filters,
    getClipsForTag,
  };
};
const formatMetadataValue = (key: string, value: string): string => {
  if (key === "Kill Count") {
    return `${value} Kill${parseInt(value) > 1 ? "s" : ""}`;
  }
  return value;
};

const shouldIncludeAsTag = (key: string, value: string): boolean => {
  const excludedValues = ["0", "None"];
  const allowedKeys = ["Kill Count"];

  return allowedKeys.includes(key) && !excludedValues.includes(value);
};
